'use client'
import { ExclamationTriangleIcon } from '@radix-ui/react-icons'
import { useTranslations } from 'next-intl'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { Alert, AlertTitle } from '@/components/ui/alert'
import './MarketoForm.css'

type MktoForms2 = {
	loadForm: (
		baseUrl: string,
		munchkinId: string,
		formId: string | number,
		callback?: (form: object) => void,
	) => void
	whenReady: (callback: (form: object) => void) => void
}

type MarketoFormProps = {
	id: number
	loadingSkeleton?: React.ReactNode
}

export const MarketoForm = ({ id, loadingSkeleton }: MarketoFormProps) => {
	const formLoadedRef = useRef(false)

	const [isLoading, setIsLoading] = useState(true)
	const [hasError, setHasError] = useState(false)

	const t = useTranslations('errors.forms')
	const formId = `mktoForm_${id}`

	// biome-ignore lint/correctness/useExhaustiveDependencies: The id is not expected to change
	useEffect(() => {
		// Prevent the form from loading multiple times
		if (formLoadedRef.current) {
			return
		}

		try {
			if (!('MktoForms2' in global)) {
				throw new Error('MktoForms2 not defined in global')
			}

			const marketo = global.MktoForms2 as MktoForms2

			// Set up the ready callback before loading the form
			marketo.whenReady(() => {
				setIsLoading(false)
				setHasError(false)
			})

			marketo.loadForm('//learn.lokalise.com', '936-ZMW-404', id)
			formLoadedRef.current = true
		} catch (error) {
			// biome-ignore lint/suspicious/noConsole: error logging for form load failure
			console.error('Failed to load Marketo form:', error)
			setHasError(true)
			setIsLoading(false)
		}
	}, [])

	return (
		<>
			{isLoading ? loadingSkeleton : null}

			{hasError ? (
				<Alert variant="destructive">
					<ExclamationTriangleIcon className="size-4" />
					<AlertTitle>{t('loadingError', { id: String(id) })}</AlertTitle>
				</Alert>
			) : null}

			{/* Always render the form element for Marketo to attach to */}
			<form id={formId} />
		</>
	)
}
